// ── Constants ─────────────────────────────────────────────────────────

export type ScheduleMode = 'daily' | 'weekly' | 'monthly' | 'every_hour';

export const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const DEFAULT_SCHEDULE = `daily 07:00 ${BROWSER_TIMEZONE}`;

export const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
export const WEEKDAYS = [
  { label: 'Monday', value: 'mon' },
  { label: 'Tuesday', value: 'tue' },
  { label: 'Wednesday', value: 'wed' },
  { label: 'Thursday', value: 'thu' },
  { label: 'Friday', value: 'fri' },
  { label: 'Saturday', value: 'sat' },
  { label: 'Sunday', value: 'sun' },
] as const;
export const MONTH_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

// ── Timezone ──────────────────────────────────────────────────────────

const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
  'Pacific/Auckland',
];

export const TIMEZONE_OPTIONS = COMMON_TIMEZONES.includes(BROWSER_TIMEZONE)
  ? COMMON_TIMEZONES
  : [BROWSER_TIMEZONE, ...COMMON_TIMEZONES];

function getUTCOffset(tz: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (!tzPart || tzPart === 'GMT') return 'UTC';
    return tzPart.replace('GMT', 'UTC');
  } catch {
    return tz;
  }
}

function getCityName(tz: string): string {
  return tz === 'UTC' ? 'UTC' : tz.split('/').pop()?.replace(/_/g, ' ') ?? tz;
}

export function formatTimezoneLabel(tz: string): string {
  return `(${getUTCOffset(tz)}) ${getCityName(tz)}`;
}

// ── Schedule parsing ──────────────────────────────────────────────────

export interface ParsedSchedule {
  mode: ScheduleMode;
  hour: string;
  weekday: string;
  monthDay: number;
  timezone: string;
}

export function parseSchedule(value: string | null): ParsedSchedule {
  const defaults = {
    hour: '07',
    weekday: 'mon',
    monthDay: 1,
    timezone: BROWSER_TIMEZONE,
  };

  if (!value) return { mode: 'daily', ...defaults };

  if (value === 'every 1 hour') return { mode: 'every_hour', ...defaults };

  const dailyMatch = value.match(/^daily (\d{2}):\d{2}(?:\s+(.+))?$/);
  if (dailyMatch) {
    return {
      mode: 'daily', ...defaults,
      hour: dailyMatch[1]!,
      timezone: dailyMatch[2]?.trim() || BROWSER_TIMEZONE,
    };
  }

  const weeklyMatch = value.match(/^weekly (\w+) (\d{2}):\d{2}(?:\s+(.+))?$/);
  if (weeklyMatch) {
    return {
      mode: 'weekly', ...defaults,
      weekday: weeklyMatch[1]!,
      hour: weeklyMatch[2]!,
      timezone: weeklyMatch[3]?.trim() || BROWSER_TIMEZONE,
    };
  }

  const monthlyMatch = value.match(/^monthly (\d{1,2}) (\d{2}):\d{2}(?:\s+(.+))?$/);
  if (monthlyMatch) {
    return {
      mode: 'monthly', ...defaults,
      monthDay: Number(monthlyMatch[1]),
      hour: monthlyMatch[2]!,
      timezone: monthlyMatch[3]?.trim() || BROWSER_TIMEZONE,
    };
  }

  // Fallback: treat unrecognised (e.g. legacy interval) as daily defaults
  return { mode: 'daily', ...defaults };
}

// ── Schedule building ─────────────────────────────────────────────────

export function buildSchedule(parsed: ParsedSchedule): string {
  const { mode, hour, weekday, monthDay, timezone } = parsed;
  const time = `${hour}:00`;
  switch (mode) {
    case 'daily':
      return `daily ${time} ${timezone}`;
    case 'weekly':
      return `weekly ${weekday} ${time} ${timezone}`;
    case 'monthly':
      return `monthly ${monthDay} ${time} ${timezone}`;
    case 'every_hour':
      return 'every 1 hour';
  }
}

// ── Schedule display ──────────────────────────────────────────────────

const ORDINAL_SUFFIX: Partial<Record<number, string>> = {
  1: 'st', 2: 'nd', 3: 'rd', 21: 'st', 22: 'nd', 23: 'rd',
};

const DAY_LABELS: Record<string, string> = Object.fromEntries(
  WEEKDAYS.map((d) => [d.value, d.label.slice(0, 3)])
);

function formatTimezoneShort(tz: string): string {
  return ` ${getCityName(tz)}`;
}

export function formatScheduleDisplay(schedule: string): string {
  const { mode, hour, weekday, monthDay, timezone } = parseSchedule(schedule);
  const time = `${hour}:00`;
  const tz = formatTimezoneShort(timezone);

  switch (mode) {
    case 'daily':
      return `Daily ${time}${tz}`;
    case 'weekly':
      return `${DAY_LABELS[weekday] ?? weekday} ${time}${tz}`;
    case 'monthly': {
      const suffix = ORDINAL_SUFFIX[monthDay] ?? 'th';
      return `${monthDay}${suffix} ${time}${tz}`;
    }
    case 'every_hour':
      return 'Every hour';
  }
}
